import { EditSpendRuleFormValues } from 'app/pages/cards/spend-rules/SpendRulesForm';
import { dollarsToLimit } from 'app/pages/cards/spend-rules/spendRuleLimits';
import { ListType } from 'app/pages/cards/spend-rules/utils';
import { newMerchantSpendRule } from 'app/pages/store/spendRulesSlice';
import { useAppDispatch, useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { useCallback } from 'react';

export function useCreateSpendRule() {
  const dispatch = useAppDispatch();
  const { selectedPaidolId } = useAppSelector(selectUserCompanies);

  const createSpendRuleHandler = useCallback(
    async ({
      name,
      spendingByCategory,
      spendingByCountry,
      spendingByCountryType,
      spendingByCategoryType,
      maximumTransactionAmount,
      monthlySpendLimit,
    }: EditSpendRuleFormValues) => {
      // Only emit a restriction when the user actually chose one — the type defaults to
      // NO_RESTRICTIONS (a truthy string), so guarding on truthiness alone sent a malformed
      // `{ 'no restrictions': [] }`. Mirrors the main create path in SpendRules.tsx.
      const hasCategory = spendingByCategoryType !== ListType.NO_RESTRICTIONS && !!spendingByCategory?.length;
      const hasCountry = spendingByCountryType !== ListType.NO_RESTRICTIONS && !!spendingByCountry?.length;

      // SEE-1401: the form holds amounts as strings ("0" is truthy). Coerce once via the shared
      // helper so empty/"0"/0 become null and a $0 all-blocking limit is never sent. Same code
      // as the main create path in SpendRules.tsx.
      const newMaximumTransactionAmount = dollarsToLimit(maximumTransactionAmount);
      const newMonthlySpendLimit = dollarsToLimit(monthlySpendLimit);

      // Nothing meaningful to persist (SpendRulesForm already disables submit in this case).
      if (!hasCategory && !hasCountry && !newMaximumTransactionAmount && !newMonthlySpendLimit) {
        return;
      }

      const data = {
        paidol_id: selectedPaidolId,
        name: name,
        ...(hasCategory
          ? {
              categories: {
                [spendingByCategoryType]: spendingByCategory,
              },
            }
          : {}),
        ...(hasCountry
          ? {
              countries: {
                [spendingByCountryType]: spendingByCountry,
              },
            }
          : {}),
        ...(newMaximumTransactionAmount ? { maximumTransactionAmount: newMaximumTransactionAmount } : {}),
        ...(newMonthlySpendLimit ? { monthlySpendLimit: newMonthlySpendLimit } : {}),
      };

      await dispatch(newMerchantSpendRule(data));
    },
    [dispatch, selectedPaidolId]
  );

  return createSpendRuleHandler;
}
